import { Controller } from "react-hook-form";
import React, { useEffect, useMemo } from "react";
import { IconButton } from "components/common";
import { FaTrashAlt } from "react-icons/fa";
import { CRM_SALUTATION } from "app_config";
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import {isValidPhoneNumber} from "libphonenumber-js";
import PropTypes from 'prop-types';

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const fieldRow = (label, children) => (
  <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3 tw-items-center">
    <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 lg:tw-text-right">
      {label}
    </div>
    <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
      {children}
    </div>
  </div>
);

const ContactForm = React.memo(({
  index, contact, control, register, errors, setValue, removeContact, updatedContact
}) => {

  const salutationOptions = useMemo(() => CRM_SALUTATION.map((option) => ({ value: option, label: option })), [])

  useEffect(() => {
    //for case edit
    if (contact) {
      Object.entries(contact).forEach(([key, value]) => {
        setValue(`${key}_${index}`, value)
      })
    }
  }, [contact, index, setValue]);

  const handleInputChange = (fieldName, value) => {
    const fieldId = generateFieldId(fieldName);
    setValue(fieldId, value);

    const updatedContact = { ...contact, [fieldName]: value };
    handleUpdateContactData(updatedContact);
  };

  const handleUpdateContactData = (updatedData) => {
    updatedContact(index, updatedData)
  }

  const handleSelectChange = (fieldName, selectedOption) => {
    setValue(fieldName, selectedOption.value, { shouldValidate: true });

    const updatedContact = { ...contact, [fieldName]: selectedOption.value };
    handleUpdateContactData(updatedContact);
  };

  const validatePhoneNumber = (number) => {
    if (number === undefined || number === "" || number === null) return undefined

    let countryCode = 'VN'
    if (!isValidPhoneNumber(number.toString(), countryCode)) {
      return "Invalid phone number";
    }
    return true
  }

  const generateFieldId = fieldName => `${fieldName}_${index}`;

  const salutation  = `salutation_${index}`;
  const first_name  = `first_name_${index}`;
  const last_name   = `last_name_${index}`;
  const email       = `email_${index}`;
  const mobile      = `mobile_${index}`;
  const position    = `position_${index}`;

  const fieldError = (error) => (
    <span className="tw-text-red-600 tw-text-xs">
      {error && error.message}
    </span>
  );

  return (
    <>
      <div className="tw-flex tw-justify-end tw-mx-3">
        <IconButton
          onClick={removeContact}
          variant="secondary"
          size="sm"
          icon={<FaTrashAlt size={12} />}
        />
      </div>

      {fieldRow('Salutation', (
        <>
          <Controller
            control={control}
            name={salutation}
            render={() => (
              <Select
                closeMenuOnSelect={true}
                options={salutationOptions}
                placeholder='Select...'
                classNamePrefix="react-select"
                onChange={selectedOption => handleSelectChange("salutation", selectedOption)}
              />
            )}
            rules={{
              required: {
                value: true,
                message: 'Salutation is required'
              }
            }}
          />
          {fieldError(errors[salutation])}
        </>
      ))}

      {fieldRow('Contact name', (
        <div className="tw-grid tw-grid-cols-12 tw-gap-2">
          <div className="tw-col-span-12 sm:tw-col-span-6">
            <input
              type="text"
              placeholder="First name"
              className={INPUT_CLASSES}
              {...register(first_name, {
                required: "First name must be required."
              })}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
            />
            {fieldError(errors[first_name])}
          </div>
          <div className="tw-col-span-12 sm:tw-col-span-6">
            <input
              type="text"
              placeholder="Last name"
              className={INPUT_CLASSES}
              {...register(last_name)}
              onChange={(e) => handleInputChange("last_name", e.target.value)}
            />
          </div>
        </div>
      ))}

      {fieldRow('Email', (
        <>
          <input
            type="email"
            placeholder="example@pebsteel.com.vn"
            className={INPUT_CLASSES}
            {...register(email, {
              //required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: 'Invalid email address'
              }
            })}
            onChange={(e) => handleInputChange("email", e.target.value)}
          />
          {fieldError(errors[email])}
        </>
      ))}

      {fieldRow('Phone number', (
        <>
          <Controller
            control={control}
            name={mobile}
            render={({field: {ref, ...field}}) => (
              <PhoneInput
                {...field}
                inputProps={{ref}}
                country='vn'
                autoFormat={true}
                countryCodeEditable={false}
                enableSearch={true}
                disableSearchIcon={true}
                inputStyle={{width: '100%'}}
                searchStyle={{width: '95%'}}
                onChange={(value) => handleInputChange('mobile', value)}
              />
            )}
            rules={{ validate: validatePhoneNumber }}
          />
          {fieldError(errors[mobile])}
        </>
      ))}

      {fieldRow('Title', (
        <input
          type="text"
          placeholder="Title"
          className={INPUT_CLASSES}
          {...register(position, {
            required: "Title must be required."
          })}
          onChange={(e) => handleInputChange("position", e.target.value)}
        />
      ))}

      <div className="tw-border-b tw-border-dashed tw-border-slate-300 tw-my-4"></div>
    </>
  )
});

ContactForm.propTypes = {
  index: PropTypes.number.isRequired,
  contact: PropTypes.object.isRequired,
  control: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  removeContact: PropTypes.func.isRequired,
  updatedContact: PropTypes.func.isRequired
}

export default ContactForm;
